import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Instagram, RefreshCw, CheckCircle2, XCircle,
  Clock, AlertTriangle, Upload, Check, Square, CheckSquare, ExternalLink, Info, ShieldAlert
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Credentials {
  id: string;
  restaurant_id: string;
  meta_app_id: string;
  meta_app_secret: string;
  access_token: string;
  catalog_id: string;
  commerce_account_id: string;
  is_active: boolean;
  token_expires_at: string | null;
  last_sync_at: string | null;
}

interface LocalProduct {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  is_available: boolean;
  sync_status?: string;
  ig_product_id?: string;
  error_message?: string;
}

const InstagramShopIntegration = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [products, setProducts] = useState<LocalProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());

  // Form state
  const [formAppId, setFormAppId] = useState("");
  const [formAppSecret, setFormAppSecret] = useState("");
  const [formAccessToken, setFormAccessToken] = useState("");
  const [formCatalogId, setFormCatalogId] = useState("");
  const [formCommerceId, setFormCommerceId] = useState("");
  const [formActive, setFormActive] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }

      const { data: restaurant } = await supabase
        .from("restaurants")
        .select("id")
        .eq("owner_id", user.id)
        .single();

      if (!restaurant) { navigate("/dashboard"); return; }
      setRestaurantId(restaurant.id);

      // Load credentials
      const { data: creds } = await supabase
        .from("instagram_shop_credentials")
        .select("*")
        .eq("restaurant_id", restaurant.id)
        .maybeSingle();

      if (creds) {
        setCredentials(creds as Credentials);
        setFormAppId(creds.meta_app_id || "");
        setFormAppSecret(creds.meta_app_secret || "");
        setFormAccessToken(creds.access_token || "");
        setFormCatalogId(creds.catalog_id || "");
        setFormCommerceId(creds.commerce_account_id || "");
        setFormActive(creds.is_active || false);
      }

      // Load products with sync status
      const { data: prods } = await supabase
        .from("products")
        .select("id, name, description, price, image_url, is_available")
        .eq("restaurant_id", restaurant.id)
        .eq("is_available", true)
        .order("name");

      const { data: mappings } = await supabase
        .from("instagram_shop_products")
        .select("product_id, ig_product_id, sync_status, error_message")
        .eq("restaurant_id", restaurant.id);

      const mappingMap = new Map(mappings?.map(m => [m.product_id, m]) || []);

      setProducts(
        (prods || []).map(p => ({
          ...p,
          sync_status: mappingMap.get(p.id)?.sync_status,
          ig_product_id: mappingMap.get(p.id)?.ig_product_id || undefined,
          error_message: mappingMap.get(p.id)?.error_message || undefined,
        }))
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const saveCredentials = async () => {
    if (!restaurantId) return;
    setSaving(true);
    try {
      const payload = {
        restaurant_id: restaurantId,
        meta_app_id: formAppId.trim(),
        meta_app_secret: formAppSecret.trim(),
        access_token: formAccessToken.trim(),
        catalog_id: formCatalogId.trim(),
        commerce_account_id: formCommerceId.trim(),
        is_active: formActive,
      };

      if (credentials) {
        await supabase
          .from("instagram_shop_credentials")
          .update(payload)
          .eq("id", credentials.id);
      } else {
        await supabase.from("instagram_shop_credentials").insert(payload);
      }

      toast({ title: "Credenciais salvas com sucesso!" });
      await loadData();
    } catch (err) {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const syncProducts = async () => {
    if (!restaurantId || selectedProducts.size === 0) return;
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("instagram-shop-sync", {
        body: {
          action: "sync_products",
          restaurant_id: restaurantId,
          product_ids: Array.from(selectedProducts),
        },
      });

      if (error) throw error;

      toast({
        title: "Sincronização concluída",
        description: data?.message || "Produtos enviados para o Instagram Shop",
      });
      setSelectedProducts(new Set());
      await loadData();
    } catch (err) {
      toast({ title: "Erro na sincronização", description: String(err), variant: "destructive" });
    } finally {
      setSyncing(false);
    }
  };

  const toggleProduct = (id: string) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  const isTokenExpiring = credentials?.token_expires_at
    ? new Date(credentials.token_expires_at).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000
    : false;

  const syncStatusIcon = (status?: string) => {
    switch (status) {
      case "synced": return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case "error": return <XCircle className="h-4 w-4 text-destructive" />;
      case "pending": return <Clock className="h-4 w-4 text-amber-500" />;
      default: return <span className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur">
        <div className="container flex h-14 items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Instagram className="h-5 w-5 text-pink-500" />
          <h1 className="text-lg font-semibold">Instagram Shop</h1>
          {credentials?.is_active && (
            <Badge className="bg-emerald-500/10 text-emerald-600 ml-auto">Ativo</Badge>
          )}
        </div>
      </header>

      <main className="container max-w-4xl py-6 space-y-6">
        {isTokenExpiring && (
          <Card className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
            <CardContent className="flex items-center gap-3 py-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  Seu Access Token está próximo de expirar
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Gere um novo token no Meta Business Suite e atualize aqui.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="config">
          <TabsList className="w-full">
            <TabsTrigger value="config" className="flex-1">Configuração</TabsTrigger>
            <TabsTrigger value="catalog" className="flex-1">Catálogo</TabsTrigger>
            <TabsTrigger value="status" className="flex-1">Status</TabsTrigger>
          </TabsList>

          {/* CONFIG TAB */}
          <TabsContent value="config" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5 text-primary" />
                  Como configurar
                </CardTitle>
                <CardDescription>
                  Siga os passos abaixo para conectar seu catálogo ao Instagram Shop
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">1</span>
                    <p>Acesse <a href="https://developers.facebook.com" target="_blank" rel="noopener" className="text-primary underline">Meta for Developers</a> e crie um App do tipo <strong>"Business"</strong>.</p>
                  </div>
                  <div className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">2</span>
                    <p>No App, vá em <strong>Configurações &gt; Básico</strong> e copie o <strong>App ID</strong> e <strong>App Secret</strong>.</p>
                  </div>
                  <div className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">3</span>
                    <p>Acesse o <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener" className="text-primary underline">Graph API Explorer</a>, selecione seu App, marque a permissão <code className="bg-muted px-1 rounded">catalog_management</code> e clique em <strong>"Generate Access Token"</strong>.</p>
                  </div>
                  <div className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">4</span>
                    <div>
                      <p>Troque o token de curta duração por um de <strong>longa duração (~60 dias)</strong>. Acesse no navegador:</p>
                      <code className="block bg-muted px-2 py-1.5 rounded text-xs mt-1 break-all">
                        https://graph.facebook.com/v19.0/oauth/access_token?grant_type=fb_exchange_token&client_id=SEU_APP_ID&client_secret=SEU_APP_SECRET&fb_exchange_token=TOKEN_CURTO
                      </code>
                      <p className="mt-1">Copie o <code className="bg-muted px-1 rounded">access_token</code> retornado.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">5</span>
                    <p>Acesse o <a href="https://business.facebook.com/commerce" target="_blank" rel="noopener" className="text-primary underline">Commerce Manager</a>, crie ou selecione um catálogo e copie o <strong>Catalog ID</strong> da URL.</p>
                  </div>
                  <div className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">6</span>
                    <p>Cole as credenciais nos campos abaixo, ative a integração e sincronize seus produtos.</p>
                  </div>
                </div>

                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 mt-4">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    <strong>Importante:</strong> O Access Token de longa duração expira a cada <strong>60 dias</strong>. Volte aqui para atualizá-lo quando necessário.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Credenciais do Meta</CardTitle>
                <CardDescription>Insira as informações do seu App e Catálogo</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>App ID</Label>
                    <Input value={formAppId} onChange={e => setFormAppId(e.target.value)} placeholder="123456789..." />
                  </div>
                  <div className="space-y-2">
                    <Label>App Secret</Label>
                    <Input type="password" value={formAppSecret} onChange={e => setFormAppSecret(e.target.value)} placeholder="••••••••" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Access Token</Label>
                    <Input type="password" value={formAccessToken} onChange={e => setFormAccessToken(e.target.value)} placeholder="EAAxxxxxxx..." />
                  </div>
                  <div className="space-y-2">
                    <Label>Catalog ID</Label>
                    <Input value={formCatalogId} onChange={e => setFormCatalogId(e.target.value)} placeholder="987654321..." />
                  </div>
                  <div className="space-y-2">
                    <Label>Commerce Account ID (opcional)</Label>
                    <Input value={formCommerceId} onChange={e => setFormCommerceId(e.target.value)} placeholder="Opcional" />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <Switch checked={formActive} onCheckedChange={setFormActive} />
                    <Label>Integração ativa</Label>
                  </div>
                  <Button onClick={saveCredentials} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Salvar Credenciais
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CATALOG TAB */}
          <TabsContent value="catalog" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Enviar Produtos</CardTitle>
                    <CardDescription>Selecione os produtos para sincronizar com o Instagram Shop</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={toggleAll}>
                      {selectedProducts.size === products.length ? (
                        <><CheckSquare className="h-4 w-4 mr-1" /> Desmarcar</>
                      ) : (
                        <><Square className="h-4 w-4 mr-1" /> Todos</>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      onClick={syncProducts}
                      disabled={syncing || selectedProducts.size === 0 || !credentials?.is_active}
                    >
                      {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                      Sincronizar ({selectedProducts.size})
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!credentials?.is_active && (
                  <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/20 p-3 rounded-lg mb-4">
                    <ShieldAlert className="h-4 w-4" />
                    Ative a integração na aba Configuração antes de sincronizar.
                  </div>
                )}

                <div className="space-y-2">
                  {products.map(product => (
                    <div
                      key={product.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedProducts.has(product.id) ? "bg-primary/5 border-primary/30" : "hover:bg-muted/50"
                      }`}
                      onClick={() => toggleProduct(product.id)}
                    >
                      <button className="shrink-0">
                        {selectedProducts.has(product.id) ? (
                          <CheckSquare className="h-5 w-5 text-primary" />
                        ) : (
                          <Square className="h-5 w-5 text-muted-foreground" />
                        )}
                      </button>

                      {product.image_url ? (
                        <img src={product.image_url} alt="" className="h-10 w-10 rounded object-cover" />
                      ) : (
                        <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">
                          <Instagram className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{product.name}</p>
                        <p className="text-xs text-muted-foreground">
                          R$ {product.price.toFixed(2).replace(".", ",")}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        {syncStatusIcon(product.sync_status)}
                        {product.sync_status === "synced" && (
                          <Badge variant="outline" className="text-emerald-600 text-[10px]">Sincronizado</Badge>
                        )}
                        {product.sync_status === "error" && (
                          <Badge variant="outline" className="text-destructive text-[10px]">Erro</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                  {products.length === 0 && (
                    <p className="text-center text-muted-foreground py-8 text-sm">
                      Nenhum produto disponível. Adicione produtos no cardápio primeiro.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* STATUS TAB */}
          <TabsContent value="status" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Status da Sincronização</CardTitle>
                    <CardDescription>
                      {credentials?.last_sync_at
                        ? `Última sincronização: ${new Date(credentials.last_sync_at).toLocaleString("pt-BR")}`
                        : "Nenhuma sincronização realizada"}
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadData}>
                    <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <p className="text-2xl font-bold">{products.length}</p>
                    <p className="text-xs text-muted-foreground">Total</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/20">
                    <p className="text-2xl font-bold text-emerald-600">{products.filter(p => p.sync_status === "synced").length}</p>
                    <p className="text-xs text-emerald-600">Sincronizados</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-destructive/5">
                    <p className="text-2xl font-bold text-destructive">{products.filter(p => p.sync_status === "error").length}</p>
                    <p className="text-xs text-destructive">Com erro</p>
                  </div>
                </div>

                <div className="space-y-2">
                  {products.filter(p => p.sync_status).map(product => (
                    <div key={product.id} className="flex items-center gap-3 p-3 rounded-lg border">
                      {syncStatusIcon(product.sync_status)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{product.name}</p>
                        {product.error_message && (
                          <p className="text-xs text-destructive">{product.error_message}</p>
                        )}
                        {product.ig_product_id && (
                          <p className="text-xs text-muted-foreground">ID: {product.ig_product_id}</p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-[10px]">
                        {product.sync_status === "synced" ? "OK" : product.sync_status === "error" ? "Erro" : "Pendente"}
                      </Badge>
                    </div>
                  ))}
                  {!products.some(p => p.sync_status) && (
                    <p className="text-center text-muted-foreground py-8 text-sm">
                      Nenhum produto sincronizado ainda. Use a aba Catálogo para enviar produtos.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default InstagramShopIntegration;
